<template>
  <div class="col-sm-12">
    <div class="card">
      <!-- Tabs -->
      <ul class="approval-tabs" role="tablist" v-if="visibleTabs.length > 0">
        <li
          v-for="tab in visibleTabs"
          :key="tab.key"
          role="presentation"
        >
          <button
            type="button"
            role="tab"
            class="approval-tab"
            :class="{ active: activeTab === tab.key }"
            :aria-selected="activeTab === tab.key"
            @click="selectTab(tab.key)"
          >
            {{ tab.title }}
          </button>
        </li>
      </ul>

      <!-- Tab content (placeholders for now) -->
      <div class="approval-content" v-if="visibleTabs.length > 0">
        <keep-alive>
          <component :is="activeComponent" :key="activeTab" v-bind="activeProps" />
        </keep-alive>
      </div>

      <div v-else class="no-data-message">
        You don't have access to any approval types.
      </div>
    </div>
  </div>
</template>

<script>

import { h } from 'vue';
import StockAdjustmentApproval from '@/pages/stock/stock_adjustment_approval/stockAdjustmentApprovalForm.vue';
import PromotionApproval from '@/pages/master_data/promotions/promotions_approve/promotion_approval_list.vue';
import ItemPriceApproval from '@/pages/master_data/items/item_price_approve/item_price_approve_form.vue';
import DealerApproval from '@/pages/master_data/dealers/dealerApproveForm.vue';
import TrustApproval from '@/pages/master_data/trust_management/approveTrustForm.vue';

const ComingSoon = {
  name: 'ComingSoon',
  props: { title: { type: String, default: '' } },
  render() {
    return h('div', { class: 'tab-placeholder' }, `${this.title} approvals coming soon`);
  },
};

export default {
  name: 'ApprovalConsole',
  components: { StockAdjustmentApproval, PromotionApproval, ItemPriceApproval, DealerApproval, TrustApproval, ComingSoon },

  data() {
    return {
      activeTab: null,
      allowedRoutes: [],

      tabs: [
        { key: 'dealer-master',      title: 'Dealer Master',      route: 'approval-console/dealer-approval' },
        { key: 'distributor-target', title: 'Distributor Target', route: 'approval-console/distributor-target-approval' },
        { key: 'stock-adjustment',   title: 'Stock Adjustment',   route: 'stock-adjustment-approval' },
        { key: 'item-price',         title: 'Item Price',         route: 'item-price-approve' },
        { key: 'promotion',          title: 'Promotion',          route: 'promotion_approval' },
        { key: 'trust',              title: 'Trust Process',      route: 'approval-console/trust-approval' },
      ],
    };
  },

  computed: {
    visibleTabs() {
      return this.tabs.filter(tab =>
        this.allowedRoutes.includes(this.normalizePath(tab.route))
      );
    },

    activeComponent() {
      const componentMap = {
        'dealer-master': 'DealerApproval',
        'stock-adjustment': 'StockAdjustmentApproval',
        'promotion': 'PromotionApproval',
        'item-price': 'ItemPriceApproval',
        'trust': 'TrustApproval',
      };
      return componentMap[this.activeTab] || 'ComingSoon';
    },

    activeProps() {
      return this.activeComponent === 'ComingSoon'
        ? { title: this.activeTabTitle }
        : {};
    },

    activeTabTitle() {
      return this.tabs.find(tab => tab.key === this.activeTab)?.title || '';
    },
  },

  methods: {
    normalizePath(path) {
      return String(path || '')
        .trim()
        .toLowerCase()
        .replace(/^\/+|\/+$/g, ''); // "/dealer-master-approval/" -> "dealer-master-approval"
    },

    /**
     * Load allowed routes for the logged-in user.
     * ASSUMPTION: stored in localStorage under "allowed_routes" as either
     *   ["/dealer-master-approval", ...]  or  [{ path: "/dealer-master-approval" }, ...]
     * Replace this method if your routes come from a store or an API.
     */
    loadAllowedRoutes() {
      try {
        const raw = JSON.parse(localStorage.getItem('allowedRoutes') || '[]');
        this.allowedRoutes = (Array.isArray(raw) ? raw : [])
          .map(item => this.normalizePath(typeof item === 'string' ? item : item?.path))
          .filter(Boolean);
      } catch (error) {
        console.error('Error reading allowed routes:', error);
        this.allowedRoutes = [];
      }
    },

    selectTab(key) {
      this.activeTab = key;
    },
  },

  created() {
    this.loadAllowedRoutes();
    // First allowed tab is selected by default
    this.activeTab = this.visibleTabs.length > 0 ? this.visibleTabs[0].key : null;
  },
};
</script>

<style scoped lang="scss">
.card {
  padding: 10px;
}

.approval-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0;
  padding: 0 10px;
  list-style: none;
  border-bottom: 2px solid #e5e7eb;
}

.approval-tab {
  padding: 12px 20px;
  margin-bottom: -2px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: #6c757d;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.approval-tab:hover {
  color: #0056b3;
  background-color: #f3f7fd;
}

.approval-tab:focus-visible {
  outline: 2px solid #007bff;
  outline-offset: -2px;
}

.approval-tab.active {
  color: #007bff;
  border-bottom-color: #007bff;
}

.approval-content {
  padding: 20px 10px;
}

.tab-placeholder {
  padding: 40px 0;
  text-align: center;
  color: #9ca3af;
}

.no-data-message {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
}

/* Responsive: tabs scroll horizontally on small screens */
@media (max-width: 768px) {
  .approval-tabs {
    flex-wrap: nowrap;
    overflow-x: auto;
  }
}
</style>